import React, { useEffect, useState } from "react"
import PropTypes from "prop-types"
import {
  Button,
  Card,
  CardBody,
  CardTitle,
  Col,
  Container,
  Form,
  FormGroup,
  Label,
  Row
} from "reactstrap"
import { connect } from "react-redux"
import { withRouter, useLocation } from "react-router-dom"

import { registerLocale, setDefaultLocale } from "react-datepicker"
import "react-datepicker/dist/react-datepicker.css"
import "assets/scss/datatables.scss"

import ptBR from "date-fns/locale/pt-BR"
import Breadcrumbs from "components/Common/Breadcrumb"
import StickerToolbarButtons from "components/Common/sticker-toolbar-buttons"
import AlertaSemVinculo from "components/Common/alerta-sem-vinculo"
import ButtonRedirect from "components/Common/button-redirect"

import usePermission from "helpers/permission/usePermission"
import { getCompanies, describeDateTime } from "helpers/utils"
import { navegarBreadCrumb } from "../../store/navigator/actions"

import FormSelectCompany from "components/Common/form-select-company"
import FormInputText from "components/Common/form-input-text"
import FormInputSelect from "components/Common/form-input-select"
import FormInputSwitch from "components/Common/form-input-switch"
import { t } from "i18next"

import {
  fetchAreaByIdRequest,
  createAreaRequest,
  updateAreaRequest,
  deleteAreaRequest,
  duplicateAreaRequest,
  clearAreaForm,
} from "../../store/areasArmazenamento/actions"
import { tipoAreaMapa, getTipoAreaOptions } from "./const"

registerLocale("ptBR", ptBR)
setDefaultLocale("ptBR")

const AreasArmazenamentoForm = props => {
  const usePermissionInsert = usePermission("areaEstoque_inserir")
  const usePermissionRemove = usePermission("areaEstoque_excluir")
  const usePermissionEdit = usePermission("areaEstoque_editar")
  const possuiVinculo = (props.user?.vinculos || []).some(v => ["A","T"].includes(v.vinculo_status))

  const [paramId, setParamId] = useState(undefined)
  const [id, setId] = useState(null)
  const [idId, setIdId] = useState(null)
  const [empresa, setEmpresa] = useState()
  const [companies, setCompanies] = useState([{label: "Todas", value: ""}])
  const [nomeArea, setNomeArea] = useState("")
  const [tipoArea, setTipoArea] = useState("")
  const [disponivel, setDisponivel] = useState("S")

  const location = useLocation()

  const normalizeTipoArea = (valor) => {
    if (!valor || typeof valor !== 'string') return ''
    const s = valor
      .toString()
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')

    const opts = getTipoAreaOptions();
        const direct = Array.isArray(opts) ? opts.find(opt => {
      const ov = String(opt?.value ?? '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
      return ov === s
    }) : null
    if (direct && direct.value != null) return direct.value

    const chave = Object.keys(tipoAreaMapa).find(k => s === k)
    if (chave) return tipoAreaMapa[chave]

    const fuzzy = Object.keys(tipoAreaMapa).find(k => s.includes(k))
    if (fuzzy) return tipoAreaMapa[fuzzy]

    return ''
  }

  useEffect(() => {
    if(paramId !== undefined) return
    const idParam = props.match.params.hasOwnProperty('id') ? props.match.params.id : null
    if (idParam && idParam !== 'novo' && idParam !== 'criar') {
      props.fetchAreaByIdRequest(idParam)
      props.navegarBreadCrumb({ title: `breadcrumb.stock.storageArea.edit`, link: location.pathname, id: idParam })
      setId(idParam)
    } else {
      const tmp = getCompanies(props.user)
      setCompanies(tmp)
      setEmpresa(tmp[0]?.value)
      props.navegarBreadCrumb({ title: `breadcrumb.stock.storageArea.new`, link: location.pathname })
      setId(null)
    }
    setParamId(idParam)
    window.scrollTo(0,0)
    return () => {}
  }, [props.match.params])

  useEffect(() => {
    if(props.currentArea && id){
      const a = props.currentArea
      setId(a.id)
      setIdId(a.idId || null)
      setEmpresa(a.idEmpresa)
      setNomeArea(a.nome_area || "")
      setTipoArea(normalizeTipoArea(a.tipo_area) || "")
      setDisponivel(a.disponivel || 'S')
    }
  }, [props.currentArea])

  useEffect(() => {
    const tmp = getCompanies(props.user)
    setCompanies(tmp)
    if(!id) setEmpresa(tmp[0]?.value)
    return ()=> setCompanies([])
  }, [])

  const salvar = () => {
    let valid = true
    valid = valid && !!empresa
    valid = valid && !!nomeArea && nomeArea.length <= 60
    valid = valid && !!tipoArea
    if(!valid){
      window.scrollTo(0,0)
      return
    }
    const payload = {
      idEmpresa: empresa,
      nome_area: nomeArea,
      tipo_area: tipoArea,
      disponivel: disponivel
    }
    if(id){
      props.updateAreaRequest(id, payload, () => props.history.push("/area-estoque"))
    } else {
      props.createAreaRequest(payload, () => props.history.push("/area-estoque"))
    }
  }

  const clonar = () => {
    if(!id) return
    props.duplicateAreaRequest(id, (novo)=>{
      props.history.push("/area-estoque")
    })
  }

  const remover = () => {
    if(!id) return
    props.deleteAreaRequest(id, ()=> props.history.push("/area-estoque"))
  }

  if(!props.user || !possuiVinculo){
    return (<AlertaSemVinculo/>)
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title={ id ? "breadcrumb.stock.storageArea.edit" : "breadcrumb.stock.storageArea.new" }/>
          <StickerToolbarButtons>
            <ButtonRedirect path={"area-estoque"} history={props.history}></ButtonRedirect>
            <Button type="button" color="primary" className="waves-effect waves-light me-2"
                    onClick={ e => salvar() }>
              { id ? t("areas.armazenamento.actions.save") : t("areas.armazenamento.actions.save") }
            </Button>
            { id && <>
              <Button type="button" color="primary" className="waves-effect waves-light me-2"
                      disabled={!(id && (usePermissionInsert))} onClick={e => clonar()}>
                {t("areas.armazenamento.actions.duplicate")}
              </Button>
              <Button type="button" color="danger" className="waves-effect waves-light me-2"
                      disabled={!(id && usePermissionRemove)} onClick={e => remover()}>
                {t("areas.armazenamento.actions.delete")}
              </Button>
            </>}
          </StickerToolbarButtons>

          <Row>
            <Col lg="12">
              <div>
                <Card>
                  <CardBody>
                    <CardTitle className="mb-4">{t("areas.armazenamento.form.cadastro")}</CardTitle>
                    <Form>
                      <div className="outer">
                        <div className="outer">
                          {idId && <FormGroup className="mb-4" row>
                            <Label className="col-form-label col-lg-2">{t("areas.armazenamento.form.codigo")}</Label>
                            <Col lg="10" className="col-form-label">{idId}</Col>
                          </FormGroup>}

                          <FormSelectCompany
                            attribute="empresa"
                            value={empresa}
                            setValue={setEmpresa}
                            options={companies.filter(c => c.value)}
                            required={true}
                            disabled={!!id}
                          />

                          <FormInputText
                            attribute="nome_area"
                            description={t("areas.armazenamento.form.nomeArea")}
                            value={nomeArea}
                            setValue={setNomeArea}
                            required={true}
                            maxLength={60}
                          />

                          <FormInputSelect
                            attribute="tipo_area"
                            description={t("areas.armazenamento.form.tipoArea")}
                            value={tipoArea}
                            setValue={setTipoArea}
                            options={ getTipoAreaOptions() }
                            required={true}
                          />

                          <FormInputSwitch
                            attribute="disponivel"
                            description={t("areas.armazenamento.form.disponivelUso")}
                            value={disponivel}
                            setValue={setDisponivel}
                          />
                        </div>
                      </div>
                    </Form>
                  </CardBody>
                </Card>

                {id && props.currentArea && (
                  <>
                    <Card className="mt-3">
                      <CardBody>
                        <CardTitle className="mb-4">{t("areas.armazenamento.form.dadosSincronizacao")}</CardTitle>
                        <Form>
                          <div className="outer">
                            <div className="outer">
                              <FormGroup className="mb-4" row>
                                <Label className="col-form-label col-lg-2">{t("areas.armazenamento.form.pendenteSincronizacao")}</Label>
                                <Col lg="10" className="col-form-label">{props.currentArea?.sincronizar === 'S' ? t("areas.armazenamento.status.pendente") : t("areas.armazenamento.status.sincronizado")}</Col>
                              </FormGroup>
                              <FormGroup className="mb-4" row>
                                <Label className="col-form-label col-lg-2">{t("areas.armazenamento.form.dataSincronizacao")}</Label>
                                <Col lg="10" className="col-form-label">{props.currentArea?.data_sincronizacao ? describeDateTime(props.currentArea.data_sincronizacao) : '-'}</Col>
                              </FormGroup>
                              <FormGroup className="mb-4" row>
                                <Label className="col-form-label col-lg-2">{t("areas.armazenamento.form.uuid")}</Label>
                                <Col lg="10" className="col-form-label">{props.currentArea?.uuid || '-'}</Col>
                              </FormGroup>
                            </div>
                          </div>
                        </Form>
                      </CardBody>
                    </Card>

                    <Card className="mt-3">
                      <CardBody>
                        <CardTitle className="mb-4">{t("areas.armazenamento.form.dadosAlteracao")}</CardTitle>
                        <Form>
                          <div className="outer">
                            <div className="outer">
                              <FormInputText
                                attribute="data_criacao"
                                description={t("areas.armazenamento.form.criacaoCadastro")}
                                value={props.currentArea?.data_criacao ? describeDateTime(props.currentArea.data_criacao) : '-'}
                                setValue={() => {}}
                                disabled={true}
                              />
                              <FormInputText
                                attribute="criado_por_nome"
                                description={t("areas.armazenamento.form.criadoPor")}
                                value={props.currentArea?.criado_por_Nome || '-'}
                                setValue={() => {}}
                                disabled={true}
                              />
                              <FormInputText
                                attribute="data_alteracao"
                                description={t("areas.armazenamento.form.alteracaoCadastro")}
                                value={props.currentArea?.data_alteracao ? describeDateTime(props.currentArea.data_alteracao) : '-'}
                                setValue={() => {}}
                                disabled={true}
                              />
                              <FormInputText
                                attribute="alterado_por_nome"
                                description={t("areas.armazenamento.form.alteradoPor")}
                                value={props.currentArea?.alterado_por_Nome || '-'}
                                setValue={() => {}}
                                disabled={true}
                              />
                            </div>
                          </div>
                        </Form>
                      </CardBody>
                    </Card>
                  </>
                )}
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  )
}

AreasArmazenamentoForm.propTypes = {
  currentArea: PropTypes.any,
  loadingForm: PropTypes.bool,
  fetchAreaByIdRequest: PropTypes.func,
  createAreaRequest: PropTypes.func,
  updateAreaRequest: PropTypes.func,
  deleteAreaRequest: PropTypes.func,
  duplicateAreaRequest: PropTypes.func,
}

const mapStatetoProps = state => {
  const { currentArea, loadingForm } = state.areasArmazenamento || {}
  const { user } = state.Login || {}
  return { currentArea, loadingForm, user }
}

export default connect(mapStatetoProps, {
  fetchAreaByIdRequest,
  createAreaRequest,
  updateAreaRequest,
  deleteAreaRequest,
  duplicateAreaRequest,
  clearAreaForm,
  navegarBreadCrumb
})(withRouter(AreasArmazenamentoForm))